import { CategoryAxis, ColumnSeries, DateAxis, LineSeries, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, Sprite } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import { BIG_NUMBER_ABBREVIATION, BLACK, BRIGHT_BLUE, CYAN, GRAY, ORANGE, PURPLE, RED, WHITE } from "../../../constants";
import LoadingEllipsis from "../../Loading/LoadingEllipsis";
import {
  columnSeriesConfiguration,
  configureCategoryAxis,
  configureDateAxis,
  configureValueAxis,
  lineSeriesConfiguration,
} from "../helpers/configurations";
import { formatDateAxisLabelToMonthly } from "../helpers/mtmConfigurations";
import { legendConfigurationOCE, oceChartConfiguration } from "../helpers/oceConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: any;
  seriesList: Array<{ name: string; field: string; type: string }>;
  theme: ThemeInt;
  chartName: string;
  isLoading: boolean;
  isCategoryAxis?: boolean;
  categoryAxisField?: string;
}

const BAR_COLORS = [CYAN, BRIGHT_BLUE, GRAY, PURPLE, ORANGE, RED];

class ExecutiveSummaryMultipleBarLineChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chartId = this.props.chartName;
  chart!: XYChart;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    if (!_.isEqual(prevProps, this.props)) {
      this.chart.dispose();
      this.initChart();
    }
  }

  componentWillUnmount() {
    this.chart.dispose();
  }

  initChart() {
    const { data, seriesList, theme, categoryAxisField, isCategoryAxis } = this.props;

    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.data = data;
    this.chart.colors.list = BAR_COLORS.map((item) => color(item));

    // Creates Legend
    this.chart.legend = legendConfigurationOCE(theme);
    this.chart.legend.reverseOrder = false;

    // Create current period date chart axes
    const dateAxis = this.chart.xAxes.push(new DateAxis());
    configureDateAxis(dateAxis, theme, true);
    dateAxis.renderer.minGridDistance = 20;
    dateAxis.dateFormats.setKey("month", "MMM");
    // dateAxis.periodChangeDateFormats.setKey("month", "MMM yy");

    // Creates value axis
    const barSeriesValueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(barSeriesValueAxis, theme);
    barSeriesValueAxis.renderer.numberFormatter.numberFormat = "#a";
    barSeriesValueAxis.renderer.numberFormatter.bigNumberPrefixes = BIG_NUMBER_ABBREVIATION;

    const lineSeriesValueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(lineSeriesValueAxis, theme);
    lineSeriesValueAxis.renderer.opposite = true;
    lineSeriesValueAxis.syncWithAxis = barSeriesValueAxis
    lineSeriesValueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return `${text}%`;
    });

    seriesList?.forEach(({ name, field, type }, idx) => {
      const series = type == "bar" ? this.chart.series.push(new ColumnSeries()) : this.chart.series.push(new LineSeries());
      series.data = data;
      series.xAxis = dateAxis;
      series.dataFields.dateX = "date";

      if (type == "bar" && series instanceof ColumnSeries) {
        columnSeriesConfiguration(series, {
          name: name,
          valueY: field,
        });
        series.yAxis = barSeriesValueAxis;
        series.columns.template.tooltipText = "{dateX.formatDate('MMM YYYY')} : {valueY.formatNumber('#,###')}";
      } else if (series instanceof LineSeries) {
        const bullet = lineSeriesConfiguration(series, {
          valueY: field,
          name: name,
        });
        series.yAxis = lineSeriesValueAxis;
        bullet.tooltipText = "{dateX.formatDate('MMM YYYY')} : {valueY.formatNumber('#,###')}%"
      }

      if (series.tooltip) {
        series.tooltip.getFillFromObject = false;
        series.tooltip.autoTextColor = false;
        series.tooltip.background.fill = color(WHITE);
        series.tooltip.label.fill = color(BLACK);
      }

      if (type == "dot") {
        series.strokeWidth = 0;
      }
    });
  }

  render() {
    return (
      <div className={"chartWrapper"}>
        <div id={this.props.chartName} className={"graph"} />
        <LoadingEllipsis isLoading={this.props.isLoading} />
      </div>
    );
  }
}

export default ExecutiveSummaryMultipleBarLineChart;
